// Archivo: ProductGrid.jsx
import React, { useState, useEffect } from "react";
import { db } from "../../firebase/firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { FiShoppingCart, FiTag, FiSearch, FiFilter } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import Skeleton from "../ProductSkeleton";

const ProductGrid = () => {
  const [dbProducts, setDbProducts] = useState([]);
  const [skeleton, setSkeleton] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const productsPerPage = 6;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "productos"));
        const productsArray = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().nombre,
          description: doc.data().descripcion || "",
          price: doc.data().precio,
          category: doc.data().categoria,
          stock: doc.data().stock,
          image: doc.data().imagenURL,
        }));
        setDbProducts(productsArray);
      } catch (error) {
        console.error("Error al obtener productos:", error);
      } finally {
        setSkeleton(false);
      }
    };

    fetchProducts();
  }, []);

  const allCategories = [...new Set(dbProducts.map((p) => p.category))].filter(Boolean);

  const toggleCategory = (category) => {
    setCurrentPage(1);
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const clearCategories = () => {
    setCurrentPage(1);
    setSelectedCategories([]);
  };

  // Filtrado
  const filteredProducts = dbProducts.filter((product) => {
    const matchCategory =
      selectedCategories.length === 0 || selectedCategories.includes(product.category);
    const matchSearch = (product.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Orden: Isopaneles primero, luego por precio asc
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const isAIsopanel = a.category === "Isopaneles";
    const isBIsopanel = b.category === "Isopaneles";
    if (isAIsopanel && !isBIsopanel) return -1;
    if (!isAIsopanel && isBIsopanel) return 1;
    return (a.price ?? 0) - (b.price ?? 0);
  });

  // Paginación
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ProductCard = ({ product }) => {
    const truncatedDescription =
      (product.description || "").length > 80
        ? `${product.description.substring(0, 80)}...`
        : product.description || "";

    return (
      <div className="relative w-full min-w-[240px] max-w-[300px] mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:scale-105 p-2 py-4">
          <img
            src={product.image}
            alt={product.name}
            className="w-auto h-60 m-auto object-cover"
            onError={(e) => {
              e.currentTarget.src = "https://images.unsplash.com/photo-1560393464-5c69a73c5770";
            }}
          />
          <div className="p-4">
            <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
            <p className="text-gray-600 text-sm mb-2">{truncatedDescription}</p>
            <div className="flex justify-between items-center mb-2">
              <span className="text-lg font-bold text-gray-600">
                USD {product.price}
              </span>
              <span
                className={`px-2 py-1 rounded-full text-sm ${
                  product.stock > 0
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {product.stock > 0 ? "En stock" : "Fuera de stock"}
              </span>
            </div>
            <p className="text-gray-500 text-sm mb-2">Stock: {product.stock}</p>
            <p className="text-gray-500 text-sm mb-4">Categoría: {product.category}</p>
            <button
              onClick={() => setSelectedProduct(product)}
              className="w-full bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700 transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <FiShoppingCart />
              Ver Producto
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ProductDetail = ({ product, onClose }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto scrollbar-hidden mx-2">
        <div className="p-6 my-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-gray-700">{product.name}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-300"
              aria-label="Cerrar detalle de producto"
            >
              <AiOutlineClose size={24} />
            </button>
          </div>
          <img
            src={product.image}
            alt={product.name}
            className="w-auto h-42 object-cover rounded-lg mb-4 mx-auto"
            onError={(e) => {
              e.currentTarget.src = "https://images.unsplash.com/photo-1560393464-5c69a73c5770";
            }}
          />
          <div className="space-y-4">
            <p className="text-gray-700">{product.description}</p>
            <div className="flex justify-between items-center">
              <span className="text-2xl font-bold text-gray-700">USD {product.price}</span>
              <span
                className={`px-3 py-1 rounded-full ${
                  product.stock > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}
              >
                {product.stock > 0 ? "En stock" : "Fuera de stock"}
              </span>
            </div>
            <p className="text-gray-600">Stock: {product.stock}</p>
            <p className="text-gray-600">Categoría: {product.category}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-20 flex flex-col md:flex-row gap-6">
      {/* Botón Mobile */}
      <div className="w-full order-first mb-6 block md:hidden">
        <div className="flex justify-center">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="bg-gray-700 text-white py-2 px-4 rounded-md w-full flex items-center justify-center gap-2"
          >
            <FiFilter />
            Filtrar productos
          </button>
        </div>
      </div>

      {/* Sidebar Desktop */}
      <aside className="hidden md:block sm:w-1/4 sm:max-w-[220px] bg-white p-4 border border-gray-200 rounded-lg shadow-sm h-fit shrink-0">
        <h2 className="text-lg font-semibold mb-4">Categorías</h2>
        <div className="space-y-3 mb-6">
          {allCategories.map((category, index) => (
            <label key={index} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedCategories.includes(category)}
                onChange={() => toggleCategory(category)}
                className="h-4 w-4"
              />
              <span>{category}</span>
            </label>
          ))}
        </div>
      </aside>

      {/* Contenido principal */}
      <main className="sm:w-3/4 flex flex-col gap-6">
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Buscar productos"
            value={searchQuery}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchQuery(e.target.value);
            }}
            className="w-full p-3 border rounded-md border-gray-300 focus:outline-none focus:border-gray-500"
          />
          <FiSearch className="absolute right-3 text-gray-500" />
        </div>

        {!skeleton ? (
          <div className="grid gap-6 grid-cols-[repeat(auto-fit,_minmax(240px,_1fr))]">
            {currentProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-[repeat(auto-fit,_minmax(240px,_1fr))]">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        )}

        {/* Paginación */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex gap-2 px-2 sm:px-0 items-center">
            <button
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded-md text-sm ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
              aria-label="Página anterior"
            >
              «
            </button>
            {[...Array(totalPages).keys()]
              .map((_, i) => i + 1)
              .filter(
                (number) =>
                  number === currentPage ||
                  number === currentPage - 1 ||
                  number === currentPage - 2 ||
                  number === currentPage + 1 ||
                  number === currentPage + 2
              )
              .map((number) => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  className={`px-3 py-1.5 rounded-md text-sm ${
                    currentPage === number
                      ? "bg-gray-700 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  aria-current={currentPage === number ? "page" : undefined}
                >
                  {number}
                </button>
              ))}
            <button
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`px-3 py-1.5 rounded-md text-sm ${
                currentPage === totalPages || totalPages === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
              aria-label="Página siguiente"
            >
              »
            </button>
          </div>
        </div>
      </main>

      {/* Detalle de producto */}
      {selectedProduct && (
        <ProductDetail product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      {/* PANEL DE FILTROS PARA MOBILE */}
      {showMobileFilters && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowMobileFilters(false)}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Sheet */}
          <div
            className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-xl p-4 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Filtrar por categoría</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 rounded-md hover:bg-gray-100"
                aria-label="Cerrar filtros"
              >
                <AiOutlineClose size={20} />
              </button>
            </div>

            <div className="space-y-3 py-2">
              {allCategories.length === 0 && (
                <p className="text-sm text-gray-500">No hay categorías disponibles.</p>
              )}
              {allCategories.map((category, idx) => (
                <label key={idx} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selectedCategories.includes(category)}
                    onChange={() => toggleCategory(category)}
                  />
                  <span className="text-gray-700">{category}</span>
                </label>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  clearCategories();
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-md"
              >
                Limpiar
              </button>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 bg-gray-700 text-white py-2 rounded-md"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;